import styles from 'css/src/components/sketch/SketchControls.module.scss';

import React, { useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import type { AspectRatio } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { AIVoice, Blueprint, BrandKit, Project } from 'js/src/client';
import { NarrativeThreadEnum } from 'js/src/client';
import { MediaSourcesModal } from 'js/src/components/app/doc-to-script/MediaSourcesModal';
import TemplatePickerModal from 'js/src/components/app/template-preview/TemplatePickerModal';
import VoiceCloningModal from 'js/src/components/app/VoiceCloningModal/VoiceCloningModal';
import VoiceEditModal from 'js/src/components/app/VoiceCloningModal/VoiceEditModal';
import VoiceSelectionModal from 'js/src/components/app/VoiceSelectionModal/VoiceSelectionModal';
import {
    BUTTON_SIZES,
    BUTTON_VARIANTS,
    TertiaryIconButton,
} from 'js/src/components/common/ui/Button';
import {
    Dropdown,
    DROPDOWN_DIVIDER,
    DROPDOWN_HEADER,
    type PlainDropdownItem,
} from 'js/src/components/common/ui/Dropdown';
import { useEnsureSelectedVoiceLoaded } from 'js/src/components/sketch/hooks/useEnsureSelectedVoiceLoaded';
import { buildDurationDropdownItems } from 'js/src/components/sketch/sketch-duration-utils';
import { buildFormatDropdownItems } from 'js/src/components/sketch/sketch-format-utils';
import { buildProjectDropdownItems } from 'js/src/components/sketch/sketch-project-utils';
import SketchBlueprintsModal from 'js/src/components/sketch/SketchBlueprintsModal';
import type { SketchControlsBadgesState } from 'js/src/components/sketch/SketchControlsBadges';
import {
    BADGE_SIZES,
    isMediaSourcesDefault,
    SketchControlsBadges,
} from 'js/src/components/sketch/SketchControlsBadges';
import type {
    SketchBlueprintsModalContext,
    TemplatePickerModalContext,
    VoiceSelectionModalContext,
} from 'js/src/data/modals';
import {
    hideModal,
    LOCKED_FEATURES,
    MODAL_IDS,
    showModal,
    VOICE_SELECTION_MODAL_SOURCE,
} from 'js/src/data/modals';
import { trackUpsellClicked } from 'js/src/libs/analytics-utils';
import type { DeepReadonly } from 'js/src/libs/consts';
import { useAppDispatch } from 'js/src/libs/RootState';
import type { StoredTemplate } from 'js/src/data/templates';
import { useHorizontalWheelScroll } from 'js/src/libs/useHorizontalWheelScroll';
import { useScrollOverflowEdges } from 'js/src/libs/useScrollOverflowEdges';

type SketchSelectData = { control: SketchControl; value?: unknown };

type SketchDropdownItem = PlainDropdownItem<SketchSelectData>;

export type SketchControl =
    | 'blueprint'
    | 'template'
    | 'narrativeThread'
    | 'duration'
    | 'format'
    | 'voice'
    | 'mediaSources'
    | 'brandKit'
    | 'project';

export type SketchControlsSource = 'chat' | 'widget';

export type ClearableSketchControl = Extract<
    SketchControl,
    | 'blueprint'
    | 'template'
    | 'narrativeThread'
    | 'duration'
    | 'format'
    | 'mediaSources'
    | 'brandKit'
>;

const SKETCH_CONTROL_TEXT: Record<SketchControl, string> = {
    blueprint: 'Blueprint',
    template: 'Template',
    narrativeThread: 'Voiceover',
    duration: 'Target duration',
    format: 'Format',
    voice: 'Change AI voice',
    mediaSources: 'Media settings',
    brandKit: 'Brand kit',
    project: 'Project',
};

export const SKETCH_CONTROL_ICON: Record<SketchControl, string> = {
    blueprint: 'blueprints',
    template: 'auto_awesome_mosaic',
    narrativeThread: 'article',
    duration: 'timer',
    format: 'aspect_ratio',
    voice: 'record_voice_over',
    mediaSources: 'perm_media',
    brandKit: 'palette',
    project: 'folder',
};

/**
 * What the new video's design is copied from: a blueprint, or a past video it should match. Either
 * fixes the voiceover setting; the rest of what it picks the user can override.
 */
export type DesignReferenceKind = 'blueprint' | 'video';

/** Height-limited menus show 7.5 rows (see Dropdown.module.scss), so cap only longer lists. */
const SCROLLING_MENU_MIN_ROWS = 7;

/** Shown on the menu items the design reference fixes, which stay visible but disabled. */
const DESIGN_DECIDED_TOOLTIP: Record<DesignReferenceKind, string> = {
    blueprint: 'Set by the selected blueprint',
    video: 'Set by the video you are matching',
};

/**
 * The project the new video is created in. Unlike the other controls it stays in the menu once
 * picked, marked with a check, and editors cannot clear it: their videos must belong to a project.
 */
export interface ProjectControlState {
    options: readonly DeepReadonly<Project>[];
    selectedProjectId: number | null;
    /** Editors must pick a project; admins may also choose "No project". */
    required: boolean;
}

export function SettingsToggleButton({
    ref,
    ...props
}: {
    onClick: VoidFunction;
    disabled: boolean;
    ref?: React.Ref<HTMLButtonElement>;
}) {
    return (
        <TertiaryIconButton
            {...props}
            ref={ref}
            variant={BUTTON_VARIANTS.OUTLINE}
            size={BUTTON_SIZES.MD}
            icon="tune"
        />
    );
}

export interface SketchControlsProps {
    className?: string;
    source: SketchControlsSource;
    dropdownDisabled: boolean;
    badgesState: SketchControlsBadgesState;
    scrollable?: boolean;
    badgeSize?: BADGE_SIZES;
    dropdownTogglePosition?: 'start' | 'inline-end';
    hideDuration?: boolean;
    hideNarrativeThread?: boolean;
    hideVoice?: boolean;
    hideBlueprint?: boolean;
    designSetBy?: DesignReferenceKind | null;
    /** The design reference has no voiceover, so there is no voice to pick. */
    designHasNoVoiceover?: boolean;
    selectedTemplateId: string | null;
    selectedAIVoiceId: string | null;
    canUseAIVoiceover: boolean;
    brandKits: readonly DeepReadonly<BrandKit>[];
    /** If undefined, the menu offers no project choice (e.g. once the video exists). */
    projectControl?: ProjectControlState;
    supportedAspectRatios: AspectRatio[] | null;
    onSelectControl: (control: SketchControl, value?: unknown) => void;
    onSelectBlueprint: (blueprint: Blueprint) => void;
    onSelectTemplate: (template: StoredTemplate) => void;
    onSelectVoice: (voice: AIVoice) => void;
    onSelectDuration: (seconds: number) => void;
    onSelectAspectRatio: (aspectRatio: AspectRatio) => void;
    onClearControl: (control: ClearableSketchControl) => void;
}

export const SketchControls = React.memo(function SketchControls({
    className,
    source,
    dropdownDisabled,
    badgesState,
    scrollable = true,
    badgeSize,
    dropdownTogglePosition = 'start',
    hideDuration = false,
    hideNarrativeThread = false,
    hideVoice = false,
    hideBlueprint = false,
    designSetBy = null,
    designHasNoVoiceover = false,
    selectedTemplateId,
    selectedAIVoiceId,
    canUseAIVoiceover,
    brandKits,
    projectControl,
    supportedAspectRatios,
    onSelectControl,
    onSelectBlueprint,
    onSelectTemplate,
    onSelectVoice,
    onSelectDuration,
    onSelectAspectRatio,
    onClearControl,
}: SketchControlsProps) {
    const dispatch = useAppDispatch();
    useEnsureSelectedVoiceLoaded(selectedAIVoiceId);
    const badgeStripRef = useRef<HTMLDivElement>(null);
    const badgeStripOverflow = useScrollOverflowEdges(badgeStripRef);
    useHorizontalWheelScroll(badgeStripRef);

    const [isMediaSourcesModalOpen, setIsMediaSourcesModalOpen] = useState(false);

    const getNarrativeControlItem = useCallback(
        (canUseAIVoiceover: boolean): SketchDropdownItem => {
            if (canUseAIVoiceover) {
                return {
                    id: 'narrative-thread',
                    nestedDropdown: {
                        toggleProps: {
                            text: 'Voiceover',
                            icon: 'article',
                        },
                        items: [
                            {
                                id: 'voiceover',
                                text: 'Voiceover',
                                icon: 'record_voice_over',
                                onSelectData: {
                                    control: 'narrativeThread' as const,
                                    value: NarrativeThreadEnum.VOICEOVER,
                                },
                            },
                            {
                                id: 'no-voiceover',
                                text: 'No voiceover',
                                icon: 'voice_over_off',
                                onSelectData: {
                                    control: 'narrativeThread' as const,
                                    value: NarrativeThreadEnum.TEXT,
                                },
                            },
                        ],
                    },
                };
            }
            return {
                id: 'voiceover-disabled',
                text: 'Voiceover',
                icon: 'record_voice_over',
                disabled: true,
                tooltipTitle: 'AI voiceover is disabled. Contact your admin to enable.',
            };
        },
        [],
    );

    const getDurationItem = useCallback((): SketchDropdownItem => {
        const durationItems = buildDurationDropdownItems((seconds, isLocked) => ({
            control: 'duration' as const,
            value: isLocked ? `locked-${seconds}` : seconds,
        }));

        return {
            id: 'duration',
            nestedDropdown: {
                toggleProps: {
                    text: SKETCH_CONTROL_TEXT['duration'],
                    icon: SKETCH_CONTROL_ICON['duration'],
                },
                items: durationItems,
                limitMenuHeight: true,
            },
        };
    }, []);

    const getFormatItem = useCallback((): SketchDropdownItem => {
        const availableRatios =
            selectedTemplateId && supportedAspectRatios
                ? supportedAspectRatios
                : VideoData.ALL_ASPECT_RATIOS;
        const formatItems = buildFormatDropdownItems(
            (aspectRatio) => ({
                control: 'format' as const,
                value: aspectRatio,
            }),
            availableRatios,
            badgesState.aspectRatio,
        );

        return {
            id: 'format',
            nestedDropdown: {
                toggleProps: {
                    text: SKETCH_CONTROL_TEXT['format'],
                    icon: SKETCH_CONTROL_ICON['format'],
                },
                items: formatItems,
            },
        };
    }, [badgesState.aspectRatio, selectedTemplateId, supportedAspectRatios]);

    const getBrandKitItem = useCallback((): SketchDropdownItem => {
        const brandKitItems: SketchDropdownItem[] = [];
        brandKits.forEach((brandKit) => {
            brandKitItems.push({
                id: `brand-kit-${brandKit.id}`,
                text: brandKit.name,
                onSelectData: {
                    control: 'brandKit' as const,
                    value: brandKit.id,
                },
            });
        });

        // If no brand kits, show "Create new brand kit" option
        if (brandKitItems.length === 0) {
            brandKitItems.push({
                id: 'create-brand-kit',
                text: 'Create new brand kit',
                icon: 'add',
                onSelectData: {
                    control: 'brandKit' as const,
                    value: -1,
                },
            });
        }

        return {
            id: 'brand-kit',
            nestedDropdown: {
                toggleProps: {
                    text: SKETCH_CONTROL_TEXT.brandKit,
                    icon: SKETCH_CONTROL_ICON.brandKit,
                },
                items: brandKitItems,
                limitMenuHeight: brandKitItems.length > SCROLLING_MENU_MIN_ROWS,
            },
        };
    }, [brandKits]);

    const getProjectItem = useCallback((control: ProjectControlState): SketchDropdownItem => {
        if (control.options.length === 0 && control.required) {
            return {
                id: 'project-unavailable',
                text: SKETCH_CONTROL_TEXT.project,
                icon: SKETCH_CONTROL_ICON.project,
                disabled: true,
                tooltipTitle: 'Ask your admin to add you to a project before creating videos.',
            };
        }

        const items = buildProjectDropdownItems(
            (projectId) => ({ control: 'project' as const, value: projectId }),
            control.options,
            control.selectedProjectId,
            { allowNoProject: !control.required },
        );
        return {
            id: 'project',
            nestedDropdown: {
                toggleProps: {
                    text: SKETCH_CONTROL_TEXT.project,
                    icon: SKETCH_CONTROL_ICON.project,
                },
                items,
                limitMenuHeight: items.length > SCROLLING_MENU_MIN_ROWS,
            },
        };
    }, []);

    const mediaSourcesBadgeVisible = Boolean(
        badgesState.mediaSources && !isMediaSourcesDefault(badgesState.mediaSources),
    );
    const narrativeBadgeVisible = Boolean(badgesState.narrativeThread);

    const settingsItems = useMemo((): readonly SketchDropdownItem[] => {
        const items: SketchDropdownItem[] = [];

        const showDuration = !hideDuration && !badgesState.durationSeconds;
        const showFormat = !badgesState.aspectRatio;
        const showBrandKit = !badgesState.brandKitName;
        const showTemplate = !badgesState.templateName;
        const showBlueprint = !hideBlueprint && !badgesState.blueprintName;
        const showNarrative = !hideNarrativeThread && !narrativeBadgeVisible;
        const showVoice = !hideVoice && !narrativeBadgeVisible;
        const showMediaSources = !mediaSourcesBadgeVisible;

        const designDecidedItem = (
            control: SketchControl,
            reference: DesignReferenceKind,
        ): SketchDropdownItem => ({
            id: `${control}-set-by-${reference}`,
            text: SKETCH_CONTROL_TEXT[control],
            icon: SKETCH_CONTROL_ICON[control],
            disabled: true,
            tooltipTitle: DESIGN_DECIDED_TOOLTIP[reference],
        });

        // "Select" subheader
        items.push({ id: DROPDOWN_HEADER, text: 'Select' });

        // Group 1: Duration, Format, Brand kit, Template, Blueprint
        if (showDuration) {
            items.push(getDurationItem());
        }
        if (showFormat) {
            items.push(getFormatItem());
        }
        if (showBrandKit) {
            items.push(getBrandKitItem());
        }
        if (showTemplate) {
            items.push({
                id: 'template',
                text: SKETCH_CONTROL_TEXT.template,
                icon: SKETCH_CONTROL_ICON.template,
                onSelectData: { control: 'template' },
            });
        }
        if (showBlueprint) {
            items.push({
                id: 'blueprint',
                text: SKETCH_CONTROL_TEXT.blueprint,
                icon: SKETCH_CONTROL_ICON.blueprint,
                onSelectData: { control: 'blueprint' },
            });
        }

        // Divider (only if we're going to show narrative/voice group below)
        if (showNarrative || showVoice) {
            items.push({ id: DROPDOWN_DIVIDER });
        }

        // Group 2: Voiceover, Voice
        if (showNarrative) {
            items.push(
                designSetBy
                    ? designDecidedItem('narrativeThread', designSetBy)
                    : getNarrativeControlItem(canUseAIVoiceover),
            );
        }
        if (showVoice) {
            items.push(
                designSetBy && designHasNoVoiceover
                    ? designDecidedItem('voice', designSetBy)
                    : {
                          id: 'voice',
                          text: SKETCH_CONTROL_TEXT.voice,
                          icon: SKETCH_CONTROL_ICON.voice,
                          onSelectData: { control: 'voice' },
                      },
            );
        }

        // Group 3: Media settings
        if (showMediaSources) {
            items.push({ id: DROPDOWN_DIVIDER });
        }
        if (showMediaSources) {
            items.push({
                id: 'mediaSources',
                text: SKETCH_CONTROL_TEXT.mediaSources,
                icon: SKETCH_CONTROL_ICON.mediaSources,
                onSelectData: { control: 'mediaSources' },
            });
        }

        // Group 4: Project
        if (projectControl) {
            items.push({ id: DROPDOWN_DIVIDER });
            items.push(getProjectItem(projectControl));
        }

        return items;
    }, [
        badgesState.aspectRatio,
        badgesState.blueprintName,
        badgesState.brandKitName,
        badgesState.durationSeconds,
        badgesState.templateName,
        canUseAIVoiceover,
        designHasNoVoiceover,
        designSetBy,
        getBrandKitItem,
        getDurationItem,
        getFormatItem,
        getNarrativeControlItem,
        getProjectItem,
        projectControl,
        hideDuration,
        hideNarrativeThread,
        hideVoice,
        hideBlueprint,
        mediaSourcesBadgeVisible,
        narrativeBadgeVisible,
    ]);

    const handleSelectVoice = useCallback(() => {
        const context: VoiceSelectionModalContext = {
            source: VOICE_SELECTION_MODAL_SOURCE.SCRIPT_EDIT_PAGE,
            initialSelectedVoiceId: selectedAIVoiceId || null,
            initialVoiceSpeed: 1,
            onApply: (voice: AIVoice) => {
                dispatch(hideModal());
                analytics.track('Sketch: Control - Voice Selected', {
                    ai_voice: voice.slug,
                    source,
                });
                onSelectVoice(voice);
            },
        };
        dispatch(
            showModal({
                modal_id: MODAL_IDS.AI_VOICE_SELECTION,
                modal_context: context,
            }),
        );
    }, [dispatch, onSelectVoice, selectedAIVoiceId, source]);

    const handleSelect = useCallback(
        (onSelectData: SketchSelectData) => {
            const control = onSelectData.control;
            analytics.track('Sketch: Control Clicked', { action: control, source });

            switch (control) {
                case 'duration': {
                    const value = onSelectData.value;
                    if (typeof value === 'string' && String(value).startsWith('locked-')) {
                        trackUpsellClicked('duration_dropdown_locked_option');
                        dispatch(
                            showModal({
                                modal_id: MODAL_IDS.FEATURE_LOCK,
                                modal_context: {
                                    featureName: LOCKED_FEATURES.MAX_VIDEO_DURATION_LIMIT,
                                },
                            }),
                        );
                    } else {
                        const seconds = Number(value);
                        if (seconds > 0) {
                            analytics.track('Sketch: Control - Duration Selected', {
                                duration_seconds: seconds,
                                source,
                            });
                            onSelectDuration(seconds);
                        }
                    }
                    break;
                }
                case 'format': {
                    const value = onSelectData.value as AspectRatio;
                    if (value) {
                        analytics.track('Sketch: Control - Aspect Ratio Selected', {
                            aspect_ratio: value,
                            source,
                        });
                        onSelectAspectRatio(value);
                    }
                    break;
                }
                case 'mediaSources':
                    setIsMediaSourcesModalOpen(true);
                    break;
                case 'blueprint': {
                    const context: SketchBlueprintsModalContext = {
                        onSelect: (blueprint: Blueprint) => {
                            dispatch(hideModal());
                            analytics.track('Sketch: Control - Blueprint Selected', {
                                blueprint_id: blueprint.id,
                                blueprint_name: blueprint.name,
                                narrative_thread: blueprint.narrative_thread,
                                template_id: blueprint.default_template_id,
                                source,
                            });
                            onSelectBlueprint(blueprint);
                        },
                    };
                    dispatch(
                        showModal({
                            modal_id: MODAL_IDS.SKETCH_BLUEPRINTS,
                            modal_context: context,
                        }),
                    );
                    break;
                }
                case 'template': {
                    const context: TemplatePickerModalContext = {
                        onSelect: (template: StoredTemplate) => {
                            dispatch(hideModal());
                            analytics.track('Sketch: Control - Template Selected', {
                                template_id: template.id,
                                template_name: template.display_name,
                                source,
                            });
                            onSelectTemplate(template);
                        },
                    };
                    dispatch(
                        showModal({
                            modal_id: MODAL_IDS.TEMPLATE_PICKER,
                            modal_context: context,
                        }),
                    );
                    break;
                }
                case 'voice': {
                    handleSelectVoice();
                    break;
                }
                case 'narrativeThread': {
                    const value = onSelectData.value;
                    analytics.track('Sketch: Control - Narrative Thread Selected', {
                        narrative_thread: value,
                        source,
                    });
                    onSelectControl(control, value);
                    break;
                }
                case 'brandKit': {
                    const value = onSelectData.value;
                    analytics.track('Sketch: Control - Brand Kit Selected', {
                        brand_kit_id: value,
                        source,
                    });
                    onSelectControl(control, value);
                    break;
                }
                case 'project': {
                    const value = onSelectData.value;
                    analytics.track('Sketch: Control - Project Selected', {
                        project_id: value,
                        source,
                    });
                    onSelectControl(control, value);
                    break;
                }
                default: {
                    const _exhaustive: never = control;
                    throw new Error(`Unhandled control: ${_exhaustive}`);
                }
            }
        },
        [
            dispatch,
            handleSelectVoice,
            onSelectAspectRatio,
            onSelectBlueprint,
            onSelectControl,
            onSelectDuration,
            onSelectTemplate,
            source,
        ],
    );

    const handleEditBadge = useCallback(
        (control: SketchControl) => {
            handleSelect({ control });
        },
        [handleSelect],
    );

    const handleBadgeDurationSelect = useCallback(
        (seconds: number) => {
            analytics.track('Sketch: Control - Duration Selected', {
                duration_seconds: seconds,
                source,
            });
            onSelectDuration(seconds);
        },
        [onSelectDuration, source],
    );

    const handleBadgeFormatSelect = useCallback(
        (aspectRatio: AspectRatio) => {
            analytics.track('Sketch: Control - Aspect Ratio Selected', {
                aspect_ratio: aspectRatio,
                source,
            });
            onSelectAspectRatio(aspectRatio);
        },
        [onSelectAspectRatio, source],
    );

    const handleBadgeBrandKitSelect = useCallback(
        (brandKitId: number) => {
            analytics.track('Sketch: Control - Brand Kit Selected', {
                brand_kit_id: brandKitId,
                source,
            });
            onSelectControl('brandKit', brandKitId);
        },
        [onSelectControl, source],
    );

    const handleClearControl = useCallback(
        (control: ClearableSketchControl) => {
            analytics.track('Sketch: Control Cleared', { action: control, source });
            onClearControl(control);
        },
        [onClearControl, source],
    );

    const dropdowns = useMemo(() => {
        const hasSettingsDropdown = settingsItems.length > 0;
        if (!hasSettingsDropdown) return null;

        return (
            <Dropdown
                breakOverflow
                ToggleComponent={SettingsToggleButton}
                toggleProps={{ disabled: dropdownDisabled }}
                items={settingsItems}
                onItemSelect={handleSelect}
            />
        );
    }, [dropdownDisabled, handleSelect, settingsItems]);

    return (
        <>
            <div className={classNames(styles['sketch-controls'], className)}>
                {dropdownTogglePosition === 'start' && dropdowns}

                <div
                    ref={badgeStripRef}
                    className={classNames('d-flex align-items-center', {
                        'overflow-x-auto overflow-y-hidden text-nowrap flex-nowrap flex-fill':
                            scrollable,
                        [styles['sketch-controls__badges']]: scrollable,
                        [styles['sketch-controls__badges--fade-start']]:
                            scrollable && badgeStripOverflow.start,
                        [styles['sketch-controls__badges--fade-end']]:
                            scrollable && badgeStripOverflow.end,
                        'flex-wrap': !scrollable,
                        'gap-1': badgeSize === BADGE_SIZES.SM,
                        'gap-2': !badgeSize || badgeSize === BADGE_SIZES.MD,
                    })}
                >
                    <SketchControlsBadges
                        state={badgesState}
                        size={badgeSize}
                        disabled={dropdownDisabled}
                        hideDuration={hideDuration}
                        supportedAspectRatios={
                            selectedTemplateId && supportedAspectRatios
                                ? supportedAspectRatios
                                : undefined
                        }
                        brandKits={brandKits}
                        onClear={handleClearControl}
                        onEdit={handleEditBadge}
                        onSelectDuration={handleBadgeDurationSelect}
                        onSelectFormat={handleBadgeFormatSelect}
                        onSelectBrandKit={handleBadgeBrandKitSelect}
                    />
                    {dropdownTogglePosition === 'inline-end' && dropdowns}
                </div>
            </div>

            <MediaSourcesModal
                isOpen={isMediaSourcesModalOpen}
                onClose={() => setIsMediaSourcesModalOpen(false)}
            />
            <VoiceSelectionModal />
            <VoiceCloningModal />
            <VoiceEditModal />
            <TemplatePickerModal />
            <SketchBlueprintsModal />
        </>
    );
});
